// Recently opened search results, persisted per account in localStorage so a
// history item navigates directly without re-running the search.
// Email tokens are stripped from stored titles — PII must not be written to
// localStorage in plaintext.
import { isLocalStorageAvailable } from 'utils/storage'
import { PageResult } from './pageDirectory'
import { SearchResult, SearchTypeKey } from './schema'
import { t } from 'translations'

const STORAGE_KEY = 'globalSearch:recentResults'
const QUERIES_STORAGE_KEY = 'globalSearch:recentQueries'

export const MAX_RECENT_RESULTS = 5
export const MAX_RECENT_QUERIES = 4

export interface RecentResult {
    // 'view' = an opened page, filtered view or command (path only, no PII).
    type: SearchTypeKey | 'view'
    title: string
    subtitle?: string
    deeplink: string
}

type RecentResultStore = Record<string, RecentResult[]>

const EMAIL_PATTERN = /\S+@\S+/

const stripEmails = (title: string): string =>
    title
        .split(' · ')
        .filter((part) => !EMAIL_PATTERN.test(part))
        .join(' · ') || title.replace(new RegExp(EMAIL_PATTERN, 'g'), '…').trim()

const readStore = (): RecentResultStore => {
    if (!isLocalStorageAvailable()) {
        return {}
    }
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    } catch {
        return {}
    }
}

export const getRecentResults = (accountSlug: string): RecentResult[] => {
    const entries = readStore()[accountSlug]
    return Array.isArray(entries) ? entries.slice(0, MAX_RECENT_RESULTS) : []
}

// setItem can still throw (e.g. quota exceeded passes the availability
// check); recents are a convenience and must never break navigation.
const safeSetItem = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value)
    } catch {
        // Recents silently stop updating when storage is full.
    }
}

const pushEntry = (accountSlug: string, entry: RecentResult): void => {
    if (!isLocalStorageAvailable()) {
        return
    }
    const store = readStore()
    const others = (store[accountSlug] ?? []).filter((recent) => recent.deeplink !== entry.deeplink)
    store[accountSlug] = [entry, ...others].slice(0, MAX_RECENT_RESULTS)
    safeSetItem(STORAGE_KEY, JSON.stringify(store))
}

// Result titles for these types are guest names — stored de-identified so no
// personal data lands in localStorage (shared front-desk browsers, GDPR).
const PERSON_RESULT_LABELS: Partial<Record<SearchTypeKey, string>> = {
    get membership() {
        return t('globalSearch.recent.membership')
    },
    get group_booking() {
        return t('globalSearch.recent.groupBooking')
    },
}

export const saveRecentResult = (accountSlug: string, result: SearchResult, deeplink: string): void => {
    const personLabel = PERSON_RESULT_LABELS[result.type]
    const title = personLabel ? `${personLabel} · …${result.id.slice(-4)}` : stripEmails(result.title)
    pushEntry(accountSlug, { type: result.type, title, deeplink })
}

// Pages, filtered views and commands: repeatable workflows, so reopening one
// is a single keystroke tomorrow. Titles/paths never contain personal data.
export const saveRecentView = (accountSlug: string, page: PageResult): void => {
    pushEntry(accountSlug, { type: 'view', title: page.title, subtitle: page.subtitle, deeplink: page.path })
}

type RecentQueryStore = Record<string, string[]>

const readQueryStore = (): RecentQueryStore => {
    if (!isLocalStorageAvailable()) {
        return {}
    }
    try {
        return JSON.parse(localStorage.getItem(QUERIES_STORAGE_KEY) || '{}')
    } catch {
        return {}
    }
}

export const getRecentQueries = (accountSlug: string): string[] => {
    const entries = readQueryStore()[accountSlug]
    return Array.isArray(entries) ? entries.slice(0, MAX_RECENT_QUERIES) : []
}

// Recorded when a query led to an action (opened result/page/view-all).
// Queries containing an email address are never stored — PII must not be
// written to localStorage in plaintext.
export const saveRecentQuery = (accountSlug: string, query: string): void => {
    const trimmed = query.trim()
    if (!isLocalStorageAvailable() || !trimmed || EMAIL_PATTERN.test(trimmed)) {
        return
    }
    const store = readQueryStore()
    const others = (store[accountSlug] ?? []).filter((entry) => entry.toLowerCase() !== trimmed.toLowerCase())
    store[accountSlug] = [trimmed, ...others].slice(0, MAX_RECENT_QUERIES)
    safeSetItem(QUERIES_STORAGE_KEY, JSON.stringify(store))
}
